/**
 * La ficha del jugador como imagen: una tarjeta que se pega en un grupo.
 *
 * La tarjeta de resultado cuenta UN partido. Esta cuenta al jugador entero — la
 * media, el nivel y el hexagono — y por eso es la que se comparte sin que haya
 * pasado nada especial: es la ficha de cromo. Sin algo asi, las estadisticas
 * solo las ve quien las tiene.
 *
 * 1200x630, el formato de las previsualizaciones de enlace, para que se vea
 * entera en cualquier chat.
 */
import "./resultCard.css";
import { t } from "../i18n/index.js";
import type { Profile } from "../game/profile.js";
import { levelFor, ratePlayer } from "../game/rating.js";

const CARD_W = 1200;
const CARD_H = 630;

const GOLD = "#ffe082";
const INK = "#f2fff5";
const MUTED = "rgba(242, 255, 245, 0.62)";
const FAINT = "rgba(242, 255, 245, 0.18)";

function hexPoint(cx: number, cy: number, i: number, r: number): [number, number] {
  const angle = ((-90 + i * 60) * Math.PI) / 180;
  return [cx + r * Math.cos(angle), cy + r * Math.sin(angle)];
}

function hexPath(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  radii: readonly number[],
): void {
  ctx.beginPath();
  radii.forEach((r, i) => {
    const [x, y] = hexPoint(cx, cy, i, r);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.closePath();
}

/** Dibuja la ficha. Separado del canvas para poder probarlo y reutilizarlo. */
export function drawPlayerCard(ctx: CanvasRenderingContext2D, profile: Profile): void {
  const rating = ratePlayer(profile.totals);
  const level = levelFor(profile.xp);

  ctx.fillStyle = "#060b08";
  ctx.fillRect(0, 0, CARD_W, CARD_H);

  const glow = ctx.createRadialGradient(300, 300, 0, 300, 300, 620);
  glow.addColorStop(0, "rgba(255, 224, 130, 0.18)");
  glow.addColorStop(1, "rgba(255, 224, 130, 0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, CARD_W, CARD_H);

  // Franjas de cesped, muy tenues: identifica el juego sin robar atencion.
  ctx.fillStyle = "rgba(90, 200, 130, 0.035)";
  for (let x = 0; x < CARD_W; x += 120) ctx.fillRect(x, 0, 60, CARD_H);

  ctx.textBaseline = "alphabetic";

  ctx.fillStyle = GOLD;
  ctx.font = "700 34px 'Bebas Neue', Impact, sans-serif";
  ctx.letterSpacing = "10px";
  ctx.fillText("FYNTA", 72, 82);
  ctx.letterSpacing = "0px";

  ctx.fillStyle = MUTED;
  ctx.font = "500 22px 'DM Sans', system-ui, sans-serif";
  ctx.textAlign = "right";
  ctx.fillText("fynta.io", CARD_W - 72, 82);
  ctx.textAlign = "left";

  // --- Izquierda: la media, que es lo que se lee desde lejos.
  ctx.fillStyle = INK;
  ctx.font = "700 210px 'Bebas Neue', Impact, sans-serif";
  ctx.fillText(String(profile.overall), 68, 300);

  const overallW = ctx.measureText(String(profile.overall)).width;
  ctx.fillStyle = GOLD;
  ctx.font = "700 30px 'Bebas Neue', Impact, sans-serif";
  ctx.letterSpacing = "6px";
  ctx.fillText(t("MEDIA"), 76, 344);
  ctx.letterSpacing = "0px";

  // El arquetipo, al lado del numero: dice de un vistazo QUE clase de jugador.
  ctx.fillStyle = GOLD;
  ctx.font = "700 44px 'Bebas Neue', Impact, sans-serif";
  ctx.letterSpacing = "3px";
  ctx.fillText(rating.archetype, 88 + overallW, 250);
  ctx.letterSpacing = "0px";
  ctx.fillStyle = MUTED;
  ctx.font = "500 21px 'DM Sans', system-ui, sans-serif";
  ctx.fillText(rating.archetypeHint, 90 + overallW, 284);

  const name = profile.identity.nickname || t("Jugador");
  ctx.fillStyle = INK;
  ctx.font = "700 62px 'Bebas Neue', Impact, sans-serif";
  ctx.fillText(name.toUpperCase(), 72, 424);

  ctx.fillStyle = MUTED;
  ctx.font = "500 24px 'DM Sans', system-ui, sans-serif";
  ctx.fillText(
    `#${profile.identity.shirtNumber} · ${level.division} · ${t("NIVEL {n}", { n: level.level })}`,
    72,
    460,
  );

  // Tres cifras de carrera, en fila.
  const line = (label: string, value: string, x: number): void => {
    ctx.fillStyle = GOLD;
    ctx.font = "700 52px 'Bebas Neue', Impact, sans-serif";
    ctx.fillText(value, x, 546);
    ctx.fillStyle = MUTED;
    ctx.font = "500 18px 'DM Sans', system-ui, sans-serif";
    ctx.fillText(label, x, 574);
  };
  line(t("PARTIDOS"), String(profile.totals.matches), 72);
  line(t("GOLES"), String(profile.totals.goals), 236);
  line(t("ASIST."), String(profile.totals.assists), 380);
  line("MVP", String(profile.totals.mvps), 512);

  // --- Derecha: el hexagono.
  const cx = 900;
  const cy = 320;
  const R = 168;

  ctx.strokeStyle = FAINT;
  ctx.lineWidth = 1;
  for (const k of [1, 0.72, 0.44]) {
    hexPath(
      ctx,
      cx,
      cy,
      Array.from({ length: 6 }, () => R * k),
    );
    ctx.stroke();
  }

  const radii = rating.attributes.map((a) => {
    const norm = Math.max(0, Math.min(1, (a.value - 40) / 59));
    return R * (0.16 + 0.84 * norm);
  });
  hexPath(ctx, cx, cy, radii);
  ctx.fillStyle = "rgba(255, 224, 130, 0.22)";
  ctx.fill();
  ctx.strokeStyle = GOLD;
  ctx.lineWidth = 3;
  ctx.stroke();

  ctx.textAlign = "center";
  rating.attributes.forEach((a, i) => {
    const [lx, ly] = hexPoint(cx, cy, i, R + 40);
    ctx.fillStyle = MUTED;
    ctx.font = "500 17px 'DM Sans', system-ui, sans-serif";
    ctx.fillText(a.label, lx, ly);
    ctx.fillStyle = INK;
    ctx.font = "700 30px 'Bebas Neue', Impact, sans-serif";
    ctx.fillText(String(a.value), lx, ly + 28);
  });
  ctx.textAlign = "left";
}

export function renderPlayerCard(profile: Profile): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = CARD_W;
  canvas.height = CARD_H;
  const ctx = canvas.getContext("2d");
  if (ctx) drawPlayerCard(ctx, profile);
  return canvas;
}

export interface PlayerCardHandle {
  dispose(): void;
}

/**
 * Monta la ficha con sus botones.
 *
 * Tres caminos, y el orden importa porque el bueno depende del aparato:
 * compartir (el unico que abre WhatsApp en movil), copiar (para pegar en un
 * chat de escritorio) y guardar, que funciona siempre y es el suelo.
 */
export function mountPlayerCard(container: HTMLElement, profile: Profile): PlayerCardHandle {
  const canvas = renderPlayerCard(profile);
  canvas.className = "result-card-canvas";
  canvas.setAttribute("aria-label", t("Tu ficha"));

  container.classList.add("result-card");
  container.innerHTML = "";
  container.appendChild(canvas);

  const canShareFile = (file: File): boolean => {
    const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean };
    return typeof nav.share === "function" && nav.canShare?.({ files: [file] }) === true;
  };

  const actions = document.createElement("div");
  actions.className = "result-card-actions";
  actions.innerHTML = `
    <button type="button" class="result-card-btn is-primary" data-card-share hidden>${t("Compartir")}</button>
    <button type="button" class="result-card-btn" data-card-copy>${t("Copiar imagen")}</button>
    <button type="button" class="result-card-btn" data-card-save>${t("Guardar PNG")}</button>
  `;
  container.appendChild(actions);

  const shareBtn = actions.querySelector("[data-card-share]") as HTMLButtonElement;
  const copyBtn = actions.querySelector("[data-card-copy]") as HTMLButtonElement;
  const saveBtn = actions.querySelector("[data-card-save]") as HTMLButtonElement;
  let resetTimer = 0;

  const flash = (btn: HTMLButtonElement, text: string): void => {
    const original = btn.dataset.label ?? btn.textContent ?? "";
    btn.dataset.label = original;
    btn.textContent = text;
    btn.classList.add("is-done");
    clearTimeout(resetTimer);
    resetTimer = window.setTimeout(() => {
      btn.textContent = original;
      btn.classList.remove("is-done");
    }, 1800);
  };

  const toBlob = (): Promise<Blob | null> =>
    new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png"));

  const fileName = (): string => `fynta-ficha-${profile.overall}.png`;

  // El boton de compartir solo aparece si el aparato sabe hacerlo con una
  // imagen. Enseñar un boton que va a fallar es peor que no tenerlo.
  void (async () => {
    const blob = await toBlob();
    if (!blob) return;
    if (canShareFile(new File([blob], fileName(), { type: "image/png" }))) {
      shareBtn.hidden = false;
    }
  })();

  shareBtn.addEventListener("click", () => {
    void (async () => {
      try {
        const blob = await toBlob();
        if (!blob) throw new Error("sin imagen");
        const file = new File([blob], fileName(), { type: "image/png" });
        await navigator.share({
          files: [file],
          title: "FYNTA",
          text: t("Mi ficha en FYNTA. fynta.io"),
        });
      } catch (err) {
        // Cancelar la hoja de compartir NO es un fallo: no hay que decir nada.
        if ((err as DOMException)?.name === "AbortError") return;
        flash(shareBtn, t("No se pudo compartir"));
      }
    })();
  });

  copyBtn.addEventListener("click", () => {
    try {
      /*
       * El `ClipboardItem` se construye con la PROMESA del blob, sin `await`
       * delante: Safari exige que la escritura al portapapeles salga del gesto
       * del usuario, y un `await` antes rompe esa cadena.
       */
      const item = new ClipboardItem({
        "image/png": toBlob().then((b) => {
          if (!b) throw new Error("sin imagen");
          return b;
        }),
      });
      void navigator.clipboard
        .write([item])
        .then(() => flash(copyBtn, t("¡Copiada!")))
        .catch(() => flash(copyBtn, t("Usa Guardar PNG")));
    } catch {
      // Firefox no admite imagenes en el portapapeles: queda Guardar PNG.
      flash(copyBtn, t("Usa Guardar PNG"));
    }
  });

  saveBtn.addEventListener("click", () => {
    void (async () => {
      const blob = await toBlob();
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName();
      a.click();
      // Liberar en el siguiente turno: revocarlo ya cancela la descarga.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      flash(saveBtn, t("Guardada"));
    })();
  });

  return {
    dispose() {
      clearTimeout(resetTimer);
      container.innerHTML = "";
    },
  };
}
